/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAvoidingView} from 'react-native-keyboard-controller';
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import type { CreateReturnItemInput } from '../infra/purchasesRepoMutations';
import type { PurchaseReceptionWithItems } from '../domain/purchaseTypes';

type ReturnFormItem = {
  receptionItemId: string;
  receptionId: string;
  productId: string;
  productName: string | null;
  productSku: string | null;
  unitOfMeasure: string | null;
  receivedQuantity: number;
  returnedQuantity: number;
  quantity: string;
  unitCost: number | null;
};

type ReturnGroup = {
  receptionId: string;
  receivedAt: string;
  items: Array<{ formIndex: number; item: ReturnFormItem }>;
};

type Props = {
  visible: boolean;
  order: { id: string; branchId: string; supplierId: string | null; supplierName: string | null } | null;
  receptions: PurchaseReceptionWithItems[];
  submitting: boolean;
  /** Una devolución por recepción (el modelo de dominio es por recepción). */
  onConfirm: (params: {
    branchId: string;
    supplierId: string | null;
    supplierName: string | null;
    reason: string;
    groups: Array<{ receptionId: string; items: CreateReturnItemInput[] }>;
  }) => void;
  onClose: () => void;
};

function shortDate(iso: string | null | undefined): string {
  const date = (iso ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '—';
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
}

export function PurchaseReturnModal({ visible, order, receptions, submitting, onConfirm, onClose }: Props) {
  const { width, height: winH } = useWindowDimensions();
  const isMobile = width < CONTENT_WIDE_BREAKPOINT;
  const backdropPadding = useModalBackdropPadding();
  const { confirm, ConfirmModal } = useOperationalConfirm();

  const [returnItems, setReturnItems] = useState<ReturnFormItem[]>([]);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (visible && receptions.length > 0) {
      const items: ReturnFormItem[] = [];
      for (const reception of receptions) {
        if (reception.status !== 'active') continue;
        for (const item of reception.items) {
          const returned = item.returnedQuantity ?? 0;
          items.push({
            receptionItemId: item.id,
            receptionId: reception.id,
            productId: item.productId ?? '',
            productName: item.productName,
            productSku: item.productSku,
            unitOfMeasure: item.unitOfMeasure,
            receivedQuantity: item.quantity,
            returnedQuantity: returned,
            quantity: '0',
            unitCost: item.unitCost,
          });
        }
      }
      setReturnItems(items);
      setReason('');
      setActionError(null);
      setSubmitAttempted(false);
    }
  }, [visible, receptions]);

  // Un grupo por recepción con items (mantiene el orden de llegada).
  const groups = useMemo<ReturnGroup[]>(() => {
    const byReception = new Map<string, ReturnGroup>();
    returnItems.forEach((item, formIndex) => {
      const existing = byReception.get(item.receptionId);
      if (existing) {
        existing.items.push({ formIndex, item });
        return;
      }
      const reception = receptions.find((r) => r.id === item.receptionId);
      byReception.set(item.receptionId, {
        receptionId: item.receptionId,
        receivedAt: reception?.receivedAt ?? '',
        items: [{ formIndex, item }],
      });
    });
    return [...byReception.values()];
  }, [returnItems, receptions]);

  const isDirty = useMemo(
    () =>
      returnItems.some((item) => (parseDecimalInput(item.quantity) ?? 0) > 0) ||
      reason.trim() !== '',
    [returnItems, reason],
  );

  const requestClose = () => {
    if (submitting) return;
    if (!isDirty) {
      onClose();
      return;
    }
    void confirm({
      title: 'Descartar devolución',
      message: 'Hay datos cargados sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      presentation: 'overlay',
    }).then((confirmed) => {
      if (confirmed) onClose();
    });
  };

  function handleItemChange(index: number, value: string) {
    setReturnItems((prev) => prev.map((item, i) => (i === index ? { ...item, quantity: value } : item)));
    setActionError(null);
  }

  function handleConfirm() {
    if (!order) return;
    setSubmitAttempted(true);

    // BUG-2 fix: validar y avisar — nunca truncar cantidades en silencio.
    const overItems = returnItems.filter((item) => {
      const qty = parseDecimalInput(item.quantity) ?? 0;
      const available = item.receivedQuantity - item.returnedQuantity;
      return qty > available;
    });
    if (overItems.length > 0) {
      setActionError(
        overItems.length === 1
          ? `"${overItems[0].productName ?? 'Producto'}" supera lo disponible para devolver. Bajá la cantidad.`
          : 'Hay cantidades que superan lo disponible para devolver. Revisá los items marcados.',
      );
      return;
    }

    const byReception = new Map<string, CreateReturnItemInput[]>();
    for (const item of returnItems) {
      const qty = parseDecimalInput(item.quantity) ?? 0;
      if (qty <= 0) continue;
      const list = byReception.get(item.receptionId) ?? [];
      list.push({
        receptionItemId: item.receptionItemId,
        productId: item.productId,
        productName: item.productName,
        productSku: item.productSku,
        quantity: qty,
        unitCost: item.unitCost,
        unitOfMeasure: item.unitOfMeasure,
      });
      byReception.set(item.receptionId, list);
    }
    const groups = [...byReception.entries()].map(([receptionId, items]) => ({
      receptionId,
      items,
    }));

    if (groups.length === 0) {
      setActionError('Ingresá al menos una cantidad mayor a 0.');
      return;
    }

    if (reason.trim() === '') {
      setActionError('Contanos el motivo de la devolución (ej: vino roto, vencido, no era lo pedido).');
      return;
    }

    onConfirm({
      branchId: order.branchId,
      supplierId: order.supplierId,
      supplierName: order.supplierName,
      reason: reason.trim(),
      groups,
    });
  }

  const content = (
    <>
      <View style={styles.receptionHeader}>
        <Text style={styles.receptionTitle}>Registrar devolución</Text>
        <Text style={styles.receptionSubtitle}>
          Indicá cuántas unidades devolvés de cada item recibido.
        </Text>
      </View>
      {groups.length === 0 ? (
        <ModalStatePanel empty emptyTitle="Sin items" emptyMessage="No hay recepciones para esta OC." />
      ) : (
        <>
          {groups.map((group) => (
            <View key={group.receptionId}>
              <View style={styles.groupHeader}>
                <Text style={styles.groupHeaderText}>
                  Recepción del {shortDate(group.receivedAt)}
                </Text>
              </View>
              {group.items.map(({ formIndex, item }) => {
                const available = item.receivedQuantity - item.returnedQuantity;
                const qty = parseDecimalInput(item.quantity) ?? 0;
                const over = qty > available;
                return (
                  <View key={`${item.receptionItemId}-${formIndex}`} style={styles.receptionItemRow}>
                    <Text style={styles.receptionItemName}>{item.productName ?? 'Producto'}</Text>
                    <Text style={styles.receptionItemPending}>
                      Recibido: {item.receivedQuantity}
                      {item.returnedQuantity > 0 ? ` · Ya devuelto: ${item.returnedQuantity}` : ''}
                      {` · Disponible: ${available}`}
                    </Text>
                    <View style={styles.receptionItemInputs}>
                      <View style={styles.flex1}>
                        <Text style={styles.inputLabel}>Devolver</Text>
                        <NumericTextInput
                          kind="decimalMoney"
                          value={item.quantity}
                          onChangeText={(v: string) => handleItemChange(formIndex, v)}
                          placeholder="0"
                          editable={available > 0}
                          style={over ? styles.inputError : undefined}
                        />
                        {over ? (
                          <Text style={styles.inputErrorText}>Máximo {available} disponibles</Text>
                        ) : null}
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          ))}
          <View style={styles.reasonRow}>
            <Text style={styles.inputLabel}>
              Motivo <Text style={styles.requiredMark}>*</Text>
            </Text>
            <TextInput
              value={reason}
              onChangeText={(v) => {
                setReason(v);
                setActionError(null);
              }}
              placeholder="Ej: producto defectuoso, vencido, etc."
              style={[
                styles.textInput,
                submitAttempted && reason.trim() === '' ? styles.inputError : undefined,
              ]}
              multiline
            />
          </View>
          {actionError ? (
            <View style={styles.errorBox}>
              <Text style={styles.actionError}>{actionError}</Text>
            </View>
          ) : null}
        </>
      )}
    </>
  );

  const actions = (
    <>
      <TouchableOpacity style={styles.cancelBtnDesk} onPress={requestClose} disabled={submitting}>
        <Text style={styles.cancelBtnDeskText}>Cancelar</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.saveBtnDesk, submitting && styles.saveBtnDisabled]}
        onPress={handleConfirm}
        disabled={submitting}
        accessibilityRole="button"
        accessibilityLabel="Confirmar devolución"
      >
        <Text style={styles.saveBtnDeskText}>Confirmar devolución</Text>
      </TouchableOpacity>
    </>
  );

  const modal = isMobile ? (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={requestClose}>
      <View style={styles.sheetBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={requestClose} />
        <KeyboardAvoidingView behavior="padding" style={styles.sheetKeyboardWrap}>
          <View style={[styles.sheetCard, { maxHeight: winH * 0.9 }]}>
            <View style={styles.grabber} />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Devolución</Text>
              <TouchableOpacity onPress={requestClose}>
                <Text style={styles.closeBtn}>×</Text>
              </TouchableOpacity>
            </View>
            <ModalAwareScrollView>{content}</ModalAwareScrollView>
            <View style={styles.sheetFooter}>
              <TouchableOpacity
                style={[styles.confirmBtnFull, submitting && styles.saveBtnDisabled]}
                onPress={handleConfirm}
                disabled={submitting}
                accessibilityRole="button"
                accessibilityLabel="Confirmar devolución"
              >
                <Text style={styles.confirmBtnText}>Confirmar devolución</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  ) : (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={requestClose}>
      <View style={[styles.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View style={[styles.modalCard, modalExperience.modalWidthStandard]}>
          <ModalScreenHeader title="Devolución a proveedor" onClose={requestClose} />
          <ModalFormScroll>{content}</ModalFormScroll>
          <View style={[styles.modalActions, modalExperience.footerActions]}>{actions}</View>
        </View>
      </View>
    </Modal>
  );

  return (
    <>
      {modal}
      {ConfirmModal}
    </>
  );
}

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  sheetKeyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
  },
  grabber: {
    width: 40,
    height: 4,
    backgroundColor: ticketColors.ruleSoft,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 8,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  sheetTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  closeBtn: {
    fontSize: 24,
    color: ticketColors.ink3,
  },
  sheetFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  confirmBtnFull: {
    padding: 14,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: ticketColors.brandPrimary,
  },
  confirmBtnText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
    fontFamily: ticketFonts.sans,
  },
  modalBackdrop: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    maxHeight: '85%',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  cancelBtnDesk: {
    padding: 10,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  cancelBtnDeskText: {
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  saveBtnDesk: {
    padding: 10,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: ticketColors.brandPrimary,
  },
  saveBtnDeskText: {
    color: '#fff',
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  receptionHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  receptionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  receptionSubtitle: {
    fontSize: 13,
    color: ticketColors.ink3,
    marginTop: 4,
    fontFamily: ticketFonts.sans,
  },
  groupHeader: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: ticketColors.tile,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  groupHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontFamily: ticketFonts.sans,
  },
  receptionItemRow: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  receptionItemName: {
    fontSize: 15,
    fontWeight: '500',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  receptionItemPending: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 2,
    fontFamily: ticketFonts.sans,
  },
  receptionItemInputs: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  flex1: {
    flex: 1,
  },
  inputLabel: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginBottom: 4,
    fontFamily: ticketFonts.sans,
  },
  requiredMark: {
    color: ticketColors.bad,
  },
  reasonRow: {
    padding: 16,
  },
  textInput: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 8,
    padding: 10,
    fontSize: 15,
    minHeight: 40,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  inputError: {
    borderColor: ticketColors.bad,
  },
  inputErrorText: {
    fontSize: 12,
    color: ticketColors.bad,
    marginTop: 4,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  errorBox: {
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  actionError: {
    color: ticketColors.warn,
    fontSize: 13,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
});
